// src/services/billingService.ts
import { apiClient } from './api';
import {
  CreateCheckoutSessionRequest,
  CreateCheckoutSessionResponse,
  CreateCreditsCheckoutRequest,
  CustomerPortalResponse,
  PlanChangeRequest,
  CancelSubscriptionRequest,
  SubscriptionResponse,
  BillingProfileResponse,
  BillingProfileUpdate,
  UsageSummary,
  InvoiceResponse,
  PaymentMethodResponse,
  PricingPageData,
  ProrationPreview,
  PlanPriceTypeMap,
  SubscriptionStatus,
  ResubscribeRequest,
  ResubscribeResponse,
  PaymentMethodValidation,
  planHierarchy
} from '@/models/Billing';

class BillingService {
  // =================== Public Pricing ===================

  async getPricingData(): Promise<PricingPageData> {
    const response = await apiClient.get<PricingPageData>('/billing/pricing');
    return response.data;
  }

  // =================== Subscription Management ===================

  async getSubscription(): Promise<SubscriptionResponse | null> {
    try {
      const response = await apiClient.get<SubscriptionResponse>('/billing/subscription');
      return response.data;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      if (error.response?.status === 404) {
        return null;
      }
      throw error;
    }
  }

  async createCheckoutSession(request: CreateCheckoutSessionRequest): Promise<CreateCheckoutSessionResponse> {
    const response = await apiClient.post<CreateCheckoutSessionResponse>(
      '/billing/create-checkout-session',
      request
    );
    return response.data;
  }

  async createCreditsCheckoutSession(request: CreateCreditsCheckoutRequest): Promise<CreateCheckoutSessionResponse> {
    const response = await apiClient.post<CreateCheckoutSessionResponse>(
      '/billing/create-credits-session',
      request
    );
    return response.data;
  }

  async createCustomerPortalSession(): Promise<CustomerPortalResponse> {
    const response = await apiClient.post<CustomerPortalResponse>('/billing/customer-portal');
    return response.data;
  }

  /**
   * Check if user can perform plan operations based on subscription status
   */
  canChangePlan(subscription: SubscriptionResponse | null): boolean {
    if (!subscription) return false;

    // Cannot change plan if subscription is cancelled
    if (subscription.status === SubscriptionStatus.CANCELED) {
      return false;
    }

    // Cannot change plan if cancellation is scheduled
    if (subscription.cancel_at_period_end) {
      return false;
    }

    return true;
  }

  /**
   * Get preview of resubscription charges including tax
   */
  async getResubscriptionPreview(priceId: string): Promise<ProrationPreview> {
    const response = await apiClient.post<ProrationPreview>(
      '/billing/resubscribe-preview',
      { new_price_id: priceId }
    );
    return response.data;
  }

  /**
   * Get resubscription preview with error handling
   */
  async getResubscriptionPreviewSafe(
    priceId: string
  ): Promise<ProrationPreview | null> {
    try {
      return await this.getResubscriptionPreview(priceId);
    } catch (error) {
      console.error('Failed to get resubscription preview:', error);
      return null;
    }
  }


  /**
   * Check if user needs to create a new subscription (vs modifying existing)
   */
  needsNewSubscription(subscription: SubscriptionResponse | null): boolean {
    if (!subscription) return true;
    return subscription.status === SubscriptionStatus.CANCELED;
  }

  /**
   * Handle plan selection - either change plan or create new subscription
   */
  async handlePlanSelection(
    subscription: SubscriptionResponse | null,
    newPriceId: string
  ): Promise<void> {
    if (this.needsNewSubscription(subscription)) {
      // Create new subscription via checkout
      const checkoutResponse = await this.createCheckoutSession({
        price_id: newPriceId
      });
      this.redirectToCheckout(checkoutResponse.session_id);
    } else {
      // Change existing subscription
      await this.changeSubscriptionPlan({
        new_price_id: newPriceId
      });
    }
  }

  async changeSubscriptionPlan(request: PlanChangeRequest): Promise<SubscriptionResponse> {
    const response = await apiClient.put<SubscriptionResponse>('/billing/change-plan', request);
    return response.data;
  }

  async cancelSubscription(request: CancelSubscriptionRequest): Promise<SubscriptionResponse> {
    const response = await apiClient.post<SubscriptionResponse>('/billing/cancel-subscription', request);
    return response.data;
  }

  /**
   * Reactivate a subscription that was set to cancel at period end.
   * This allows users to keep their subscription instead of letting it cancel.
   * 
   * @returns Updated subscription without cancellation
   * @throws Error if reactivation fails or subscription not scheduled for cancellation
   */
  async reactivateSubscription(): Promise<SubscriptionResponse> {
    const response = await apiClient.post<SubscriptionResponse>('/billing/reactivate-subscription');
    return response.data;
  }

  /**
 * Smart resubscription - uses existing payment method if available
 * 
 * @param request - Contains the price_id and optional force_checkout flag
 * @returns Promise with resubscription result
 */
  async resubscribe(
    request: ResubscribeRequest
  ): Promise<ResubscribeResponse> {
    try {
      const response = await apiClient.post<ResubscribeResponse>(
        '/billing/resubscribe',
        request
      );

      return response.data;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      console.error('Failed to resubscribe:', error);
      throw new Error(
        error.response?.data?.detail ||
        'Failed to resubscribe'
      );
    }
  }

  /**
   * Cancel only the scheduled plan change without canceling the entire subscription.
   * This allows users to keep their current plan instead of downgrading.
   * 
   * @param scheduleId - The Stripe subscription schedule ID
   * @returns Updated subscription without the scheduled change
   */
  async cancelScheduledPlanChange(scheduleId: string): Promise<SubscriptionResponse> {
    const response = await apiClient.post<SubscriptionResponse>(
      '/billing/cancel-scheduled-change',
      { stripe_schedule_id: scheduleId }
    );
    return response.data;
  }

  // =================== Usage and Analytics ===================

  async getUsageSummary(): Promise<UsageSummary> {
    const response = await apiClient.get<UsageSummary>('/billing/usage-summary');
    return response.data;
  }

  // =================== Billing Profile Management ===================

  async getBillingProfile(): Promise<BillingProfileResponse | null> {
    try {
      const response = await apiClient.get<BillingProfileResponse>('/billing/profile');
      return response.data;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      if (error.response?.status === 404) {
        return null;
      }
      throw error;
    }
  }

  async updateBillingProfile(profileUpdate: BillingProfileUpdate): Promise<BillingProfileResponse> {
    const response = await apiClient.put<BillingProfileResponse>('/billing/profile', profileUpdate);
    return response.data;
  }

  // =================== Invoice Management ===================

  async getInvoices(limit: number = 20): Promise<InvoiceResponse[]> {
    const response = await apiClient.get<InvoiceResponse[]>(`/billing/invoices?limit=${limit}`);
    return response.data;
  }

  async getInvoiceDownloadUrl(invoiceId: string): Promise<string> {
    const response = await apiClient.get(`/billing/invoices/${invoiceId}/download`);
    return response.data.url;
  }

  // =================== Payment Methods ===================

  async getPaymentMethods(): Promise<PaymentMethodResponse[]> {
    const response = await apiClient.get<PaymentMethodResponse[]>('/billing/payment-methods');
    return response.data;
  }

  /**
   * Validate payment method for resubscription
   */
  async validatePaymentMethod(): Promise<PaymentMethodValidation> {
    const response = await apiClient.get<PaymentMethodValidation>(
      '/billing/validate-payment-method'
    );
    return response.data;
  }

  // =================== Proration Preview ===================

  async getProrationPreview(newPriceId: string): Promise<ProrationPreview> {
    const response = await apiClient.post<ProrationPreview>('/billing/proration-preview', {
      new_price_id: newPriceId
    });
    return response.data;
  }

  /**
   * Get proration preview with error handling for cancelled subscriptions
   */
  async getProrationPreviewSafe(
    subscription: SubscriptionResponse | null,
    newPriceId: string
  ): Promise<ProrationPreview | null> {
    // Cannot get proration for cancelled or non-existent subscriptions
    if (!subscription || subscription.status === SubscriptionStatus.CANCELED) {
      return null;
    }

    try {
      return await this.getProrationPreview(newPriceId);
    } catch (error) {
      console.error('Failed to get proration preview:', error);
      return null;
    }
  }

  // =================== Utility Methods ===================

  /**
   * Redirects to Stripe Checkout
   */
  redirectToCheckout(sessionId: string): void {
    window.location.href = `https://checkout.stripe.com/c/pay/${sessionId}`;
  }

  /**
   * Opens Stripe Customer Portal in new tab
   */
  openCustomerPortal(): Promise<void> {
    return this.createCustomerPortalSession().then(({ url }) => {
      window.open(url, '_blank');
    });
  }

  /**
   * Format currency for display
   */
  formatCurrency(amountInCents: number, currency: string = 'EUR'): string {
    return new Intl.NumberFormat('en-EU', {
      style: 'currency',
      currency: currency.toUpperCase(),
    }).format(amountInCents / 100);
  }

  /**
   * Calculate usage percentage with safe division
   */
  calculateUsagePercentage(used: number, total: number): number {
    if (total === 0) return 0;
    return Math.min(Math.round((used / total) * 100), 100);
  }

  /**
   * Get plan display name from price ID
   */
  getPlanNameFromPriceId(priceId: string): string {
    return PlanPriceTypeMap[priceId] || 'Unknown Plan';
  }

  /**
   * Check if user can upgrade to a specific plan
   */
  /**
   * Check if user can upgrade to a specific plan
   */
  canUpgradeTo(currentPriceId: string | null, targetPriceId: string): boolean {
    // If no current subscription, not an upgrade
    if (!currentPriceId) {
      return false;
    }

    const currentIndex = planHierarchy.indexOf(currentPriceId);
    const targetIndex = planHierarchy.indexOf(targetPriceId);

    return currentIndex >= 0 && targetIndex > currentIndex;
  }

  /**
   * Check if user can downgrade to a specific plan
   */
  canDowngradeTo(currentPriceId: string | null, targetPriceId: string): boolean {
    // If no current subscription, not a downgrade
    if (!currentPriceId) {
      return false;
    }

    const currentIndex = planHierarchy.indexOf(currentPriceId);
    const targetIndex = planHierarchy.indexOf(targetPriceId);

    return currentIndex >= 0 && targetIndex >= 0 && targetIndex < currentIndex;
  }
}

// Export singleton instance
export const billingService = new BillingService();
export default billingService;